import { Alert, Box, Button } from "@mui/material";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { getDoctorProfile } from "@/api/userApi";
import type { DoctorProfileStatus } from "@/types/domain";

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps doctor-only pages (appointments, availability, …) so that they
 * are fully hidden behind a high-visibility amber notice until the
 * admin has approved the doctor's profile.
 *
 * Keeping the gate in a single place means we can't accidentally leave a
 * page accessible when adding new flows in the future.
 */
export function DoctorApprovalGate({ children }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["doctor-profile"],
    queryFn: getDoctorProfile,
  });

  if (isLoading) return <>{children}</>;

  const status: DoctorProfileStatus = data?.profileStatus ?? "INCOMPLETE";
  if (status === "APPROVED") return <>{children}</>;

  const message =
    status === "PENDING_REVIEW"
      ? "Your profile is under admin review. We usually respond within 2 business days."
      : status === "REJECTED"
        ? `Your verification was rejected${
            data?.rejectionReason ? `: ${data.rejectionReason}` : ""
          }. Please update your details and resubmit.`
        : "Complete your profile and submit your medical-council registration for admin review. Patients won't see you until you're approved.";

  return (
    <Box>
      <Alert
        severity={status === "REJECTED" ? "error" : "warning"}
        variant="filled"
        icon={
          status === "PENDING_REVIEW" ? (
            <HourglassTopIcon fontSize="inherit" />
          ) : undefined
        }
        sx={{
          fontSize: "1rem",
          fontWeight: 600,
          alignItems: "center",
          boxShadow: 2,
        }}
        action={
          <Button
            component={RouterLink}
            to="/doctor/onboarding"
            color="inherit"
            variant="outlined"
            size="small"
            sx={{ borderColor: "rgba(255,255,255,0.7)", fontWeight: 700 }}
          >
            {status === "PENDING_REVIEW" ? "View status" : "Complete now"}
          </Button>
        }
      >
        {message}
      </Alert>
    </Box>
  );
}
